import Swiper from 'swiper';
import type { SwiperOptions } from 'swiper/types';
import { Navigation } from 'swiper/modules';
import { Carousel } from 'bootstrap';

import { forceDisposeBootstrapCarousel } from '@/js/utils/dispose-bootstrap-carousel';

import 'swiper/css';

const SLIDE_GAP = 16;
const MIN_LOOP_SLIDES = 6;
const PADDED_SLIDE_CLASS = 'cluster-gallery-swiper__slide--pad';

export class ClusterGallerySwiperWidget {
  private readonly root: HTMLElement;
  private readonly source: HTMLElement;
  private swiper: Swiper | null = null;
  private swiperEl: HTMLElement | null = null;
  private swiperBox: HTMLElement | null = null;
  private uniqueSlideCount = 0;

  constructor(root: HTMLElement, source: HTMLElement) {
    this.root = root;
    this.source = source;
  }

  public init(): void {
    if (!this.source.children.length) return;

    this.uniqueSlideCount = this.getUniqueSlides().length;

    this.root.classList.toggle(
      'edge-cards-carousel__carousel--single-slide',
      this.uniqueSlideCount <= 1,
    );

    this.destroy();
    this.detachBootstrap();
    this.mountSwiper();
    this.initSwiper();
  }

  public destroy(): void {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
    this.swiperBox?.remove();
    this.swiperBox = null;
    this.swiperEl = null;

    if (!this.root.querySelector('.carousel-inner')) {
      const inner = document.createElement('div');
      inner.className = 'carousel-inner';
      const controls = this.root.querySelector('.carousel-controls');
      if (controls) {
        controls.insertAdjacentElement('afterend', inner);
      } else {
        this.root.appendChild(inner);
      }
    }
  }

  private detachBootstrap(): void {
    forceDisposeBootstrapCarousel(this.root);
    Carousel.getInstance(this.root)?.dispose();

    this.root.classList.remove('carousel', 'slide', 'carousel-fade');
    this.root.removeAttribute('data-bs-ride');
    this.root.removeAttribute('data-bs-interval');

    this.root
      .querySelectorAll<HTMLButtonElement>('.carousel-control-prev, .carousel-control-next')
      .forEach((btn) => {
        btn.removeAttribute('data-bs-target');
        btn.removeAttribute('data-bs-slide');
        btn.removeAttribute('data-bs-toggle');
      });
  }

  private getUniqueSlides(): HTMLElement[] {
    const seen = new Set<string>();
    const slides: HTMLElement[] = [];

    Array.from(this.source.children).forEach((item) => {
      const img = item.querySelector('img');
      const key = img?.getAttribute('src') ?? item.outerHTML;
      if (seen.has(key)) return;
      seen.add(key);
      slides.push(item);
    });

    return slides.length ? slides : Array.from(this.source.children);
  }

  private expandSlidesForLoop(slides: HTMLElement[]): HTMLElement[] {
    if (slides.length <= 1) return slides;

    const result = [...slides];
    while (result.length < MIN_LOOP_SLIDES) {
      result.push(slides[result.length % slides.length]!);
    }

    return result;
  }

  private mountSwiper(): void {
    this.root.querySelector('.carousel-inner')?.remove();

    const box = document.createElement('div');
    box.className = 'cluster-gallery-swiper-box';

    const swiperEl = document.createElement('div');
    swiperEl.className = 'swiper cluster-gallery-swiper';

    const wrapper = document.createElement('div');
    wrapper.className = 'swiper-wrapper';

    const uniqueSlides = this.getUniqueSlides();
    const slidesForMount = this.expandSlidesForLoop(uniqueSlides);

    slidesForMount.forEach((item, index) => {
      const slide = document.createElement('div');
      slide.className = 'swiper-slide';
      if (index >= uniqueSlides.length) {
        slide.classList.add(PADDED_SLIDE_CLASS);
        slide.setAttribute('aria-hidden', 'true');
      }
      slide.appendChild(item.cloneNode(true));
      wrapper.appendChild(slide);
    });

    swiperEl.appendChild(wrapper);
    box.appendChild(swiperEl);

    const controls = this.root.querySelector('.carousel-controls');
    if (controls) {
      controls.insertAdjacentElement('afterend', box);
    } else {
      this.root.prepend(box);
    }

    this.swiperBox = box;
    this.swiperEl = swiperEl;
  }

  private initSwiper(): void {
    if (!this.swiperEl) return;

    const prevBtn = this.root.querySelector<HTMLElement>('.carousel-control-prev');
    const nextBtn = this.root.querySelector<HTMLElement>('.carousel-control-next');
    const hasNavigation = this.uniqueSlideCount > 1;

    const options: SwiperOptions = {
      modules: [Navigation],
      slidesPerView: 1.2,
      slidesPerGroup: 1,
      spaceBetween: SLIDE_GAP,
      centeredSlides: true,
      speed: 600,
      grabCursor: true,
      watchOverflow: false,
      loop: this.uniqueSlideCount > 1,
      loopAddBlankSlides: false,
      initialSlide: this.uniqueSlideCount > 1 ? 1 : 0,
      breakpoints: {
        758: {
          slidesPerView: 2.4,
          centeredSlides: true,
        },
        993: {
          slidesPerView: 'auto',
          centeredSlides: false,
        },
      },
      ...(hasNavigation && {
        navigation: {
          prevEl: prevBtn,
          nextEl: nextBtn,
          addIcons: false,
        },
      }),
    };

    this.swiper = new Swiper(this.swiperEl, options);

    if (this.uniqueSlideCount > 1 && this.swiper.params.loop) {
      this.swiper.slideToLoop(1, 0);
    }
  }
}
